import { useRef, useState } from "react";
import { toast } from "sonner";
import {
  MAX_FILES, MAX_IMG_BYTES, MAX_DOC_BYTES,
  guessMime, kindFor, docKind,
} from "@/lib/chatAttachments";

/**
 * useChatAttachments — owns the attachment state + all interaction handlers
 * for the composer: click-to-pick, paste, drag, drop, remove.
 *
 * Exposes:
 *   attachments      — current staged files [{name, data, mime, kind, ...}]
 *   setAttachments   — direct setter (used by send() to clear after upload)
 *   dragOver         — true when a file-drag is hovering the chat surface
 *   onPick(fileList) — programmatic file selection (from <input> or paste)
 *   onPaste(e)       — clipboard paste handler; adds any files it finds
 *   dragProps        — { onDragEnter, onDragOver, onDragLeave, onDrop }
 *                      spread these onto the root chat container.
 *   removeAttachment(i) — remove a single staged file by index.
 */
export default function useChatAttachments() {
  const [attachments, setAttachments] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const dragDepth = useRef(0);

  const onPick = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const room = MAX_FILES - attachments.length;
    if (room <= 0) return toast.error(`Up to ${MAX_FILES} files per message.`);
    const next = [];
    for (const f of files.slice(0, room)) {
      const mime = guessMime(f);
      const kind = kindFor(mime);
      if (!kind) {
        toast.error(`${f.name || "file"}: unsupported type.`); continue;
      }
      const cap = kind === "image" ? MAX_IMG_BYTES : MAX_DOC_BYTES;
      if (f.size > cap) {
        toast.error(`${f.name || "file"}: max ${cap / (1024*1024)} MB.`); continue;
      }
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onerror = rej;
        r.onload = () => res(String(r.result).split(",")[1]);
        r.readAsDataURL(f);
      });
      next.push({
        name: f.name || `pasted-${Date.now()}.png`,
        data: b64,
        mime,
        kind,           // "image" | "doc"
        docKind: kind === "doc" ? docKind(mime) : null,
        size: f.size,
        preview: kind === "image" ? URL.createObjectURL(f) : null,
      });
    }
    setAttachments(a => [...a, ...next]);
    if (next.length) toast.success(`${next.length} file${next.length === 1 ? "" : "s"} attached`);
    // Reset the file input inside the composer so re-picking the same file works.
    const fileInput = document.querySelector('[data-testid="chat-attach-input"]');
    if (fileInput) fileInput.value = "";
  };

  const onPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (const it of items) {
      if (it.kind === "file") files.push(it.getAsFile());
    }
    if (files.length) {
      e.preventDefault();
      onPick(files);
    }
  };

  const hasDragFiles = (e) => Array.from(e.dataTransfer?.items || []).some(it => it.kind === "file");

  const dragProps = {
    onDragEnter: (e) => { if (hasDragFiles(e)) { e.preventDefault(); dragDepth.current += 1; setDragOver(true); } },
    onDragOver:  (e) => { if (hasDragFiles(e)) { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; } },
    onDragLeave: (e) => { if (hasDragFiles(e)) { dragDepth.current = Math.max(0, dragDepth.current - 1); if (dragDepth.current === 0) setDragOver(false); } },
    onDrop: (e) => {
      if (!hasDragFiles(e)) return;
      e.preventDefault();
      dragDepth.current = 0;
      setDragOver(false);
      const files = Array.from(e.dataTransfer.files || []);
      if (files.length) onPick(files);
    },
  };

  const removeAttachment = (i) => setAttachments(a => {
    const gone = a[i];
    if (gone?.preview) URL.revokeObjectURL(gone.preview);
    return a.filter((_, j) => j !== i);
  });

  return { attachments, setAttachments, dragOver, onPick, onPaste, dragProps, removeAttachment };
}
